import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AdvancedSearch from "@components/Search/AdvancedSearch";
import queryKeys from "@configs/react-query/query-key";
import { useMutationData } from "@hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { AdjustIcon } from "@components/Icons";
import TableWithPagination from "@components/Tables/TableWithPagination";
import { showErrorToast } from "@configs/react-toastify/snackbar";
import { PrimaryButton } from "@components/Buttons";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import useItemsPerPage from "@hooks/useItemsPerPage";
import { useAdvancedSearch } from "@hooks/useAdvancedSearch";
import { resolveSearchCriteria } from "@utils/searchHelpers";
import useEscapeKey from "@hooks/useEscapeKey";

const INITIAL_COMPONENT_SEARCH_CRITERIA = {
    searchTerm: "",
};

const SelectComponentModal = ({ isOpen, onClose, onSelect, selectedComponents = [], mode = "default", projectPreviewComponents = [], "data-testid": dataTestId = "select-component-modal" }) => {
    const fieldInputTestId = (name) => `${dataTestId}-field-${name}-input`;
    const { t } = useTranslation();
    const { colors } = useTheme();
    const [componentList, setComponentList] = useState([]);
    const [totalComponentData, setTotalComponentData] = useState(0);
    const [totalComponentPages, setTotalComponentPages] = useState(0);
    const [searchComponentCriteria, setSearchComponentCriteria] = useState(INITIAL_COMPONENT_SEARCH_CRITERIA);
    const [debouncedSearchCriteria, setDebouncedSearchCriteria] = useState(searchComponentCriteria);
    const [currentComponentPage, setCurrentComponentPage] = useState(1);
    const [tempSelectedComponentSeq, setTempSelectedComponentSeq] = useState([]);
    const [selectedComponentItems, setSelectedComponentItems] = useState([]);
    const [allSelectedComponents, setAllSelectedComponents] = useState([]); // Store all selected components from all pages
    const [isSearching, setIsSearching] = useState(false);
    const debounceTimerRef = useRef(null);
    const isFirstSearchRenderRef = useRef(true);
    const wasOpenRef = useRef(false);
    const activeRequestIdRef = useRef(0);
    const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.PREVIEW_LIST_ADD_COMPONENT_LIST, DEFAULT_ITEMS_PER_PAGE);
    const [isInitialized, setIsInitialized] = useState(false);
    const [selectedComponentsFromAllPages, setSelectedComponentsFromAllPages] = useState(new Map()); // Track components from all pages

    const isComponentAlreadySelected = (component) => {
        if (mode === "preview-detail") {
            // For PreviewDetail mode, use 6 specific attributes for comparison
            let isSelected = selectedComponents.some(selectedComp =>
                (selectedComp.componentName || '') === (component.componentName || '') &&
                (selectedComp.componentVersion || '') === (component.componentVersion || '') &&
                (selectedComp.licenseId || '') === (component.licenseId || '') &&
                (selectedComp.licenseIdentifier || '') === (component.licenseIdentifier || '') &&
                (selectedComp.licenseName || '') === (component.licenseName || '') &&
                (selectedComp.licenseTypeCd || '') === (component.licenseTypeCd || '')
            );

            if (!isSelected) {
                isSelected = allSelectedComponents.some(selectedComp =>
                    (selectedComp.componentName || '') === (component.componentName || '') &&
                    (selectedComp.componentVersion || '') === (component.componentVersion || '') &&
                    (selectedComp.licenseId || '') === (component.licenseId || '') &&
                    (selectedComp.licenseIdentifier || '') === (component.licenseIdentifier || '') &&
                    (selectedComp.licenseName || '') === (component.licenseName || '') &&
                    (selectedComp.licenseTypeCd || '') === (component.licenseTypeCd || '')
                );
            }

            if (!isSelected) {
                isSelected = tempSelectedComponentSeq.includes(component.componentSeq);
            }

            // Check against projectPreviewComponents for tracking (ONLY for preview-detail mode)
            if (!isSelected && projectPreviewComponents.length > 0) {
                isSelected = projectPreviewComponents.some(projectComp =>
                    (projectComp.componentName || '') === (component.componentName || '') &&
                    (projectComp.componentVersion || '') === (component.componentVersion || '') &&
                    (projectComp.licenseId || '') === (component.licenseId || '') &&
                    (projectComp.licenseIdentifier || '') === (component.licenseIdentifier || '') &&
                    (projectComp.licenseName || '') === (component.licenseName || '') &&
                    (projectComp.licenseTypeCd || '') === (component.licenseTypeCd || '')
                );
            }

            return isSelected;
        } else {
            const isAddPreviewMode = mode === "add-preview" || (selectedComponents.length > 0 && selectedComponents.some(comp => comp.hasOwnProperty('componentSeq') && !comp.hasOwnProperty('projectPreviewComponentSeq')));

            if (isAddPreviewMode) {
                // For AddPreview mode, use componentSeq for comparison ONLY
                // DO NOT check projectPreviewComponents for add-preview mode
                let isSelected = selectedComponents.some(selectedComp => selectedComp.componentSeq === component.componentSeq);

                if (!isSelected) {
                    isSelected = allSelectedComponents.some(selectedComp => selectedComp.componentSeq === component.componentSeq);
                }

                if (!isSelected) {
                    isSelected = tempSelectedComponentSeq.includes(component.componentSeq);
                }

                return isSelected;
            } else {
                // For other modes, use full comparison
                return selectedComponents.some(selectedComp =>
                    (selectedComp.componentName || '') === (component.componentName || '') &&
                    (selectedComp.componentVersion || '') === (component.componentVersion || '') &&
                    (selectedComp.licenseId || '') === (component.licenseId || '') &&
                    (selectedComp.licenseIdentifier || '') === (component.licenseIdentifier || '') &&
                    (selectedComp.licenseName || '') === (component.licenseName || '') &&
                    (selectedComp.licenseTypeCd || '') === (component.licenseTypeCd || '') &&
                    (selectedComp.cpe || '') === (component.cpe || '') &&
                    (selectedComp.copyright || '') === (component.copyright || '') &&
                    (selectedComp.weaknessCd || '') === (component.weaknessCd || '')
                    // (selectedComp.homepageUrl || '') === (component.homepageUrl || '') &&
                    // (selectedComp.downloadUrl || '') === (component.downloadUrl || '') &&
                    // (selectedComp.description || '') === (component.description || '') &&
                    // (selectedComp.remark || '') === (component.remark || '') &&
                );
            }
        }
    };

    const componentAdvancedSearchFields = [
        {
            name: "componentVersion",
            label: "Component Version",
            type: "text",
            placeholder: t("common.component-version"),
        },
        {
            name: "licenseIdentifier",
            label: "Component License",
            type: "text",
            placeholder: t("common.component-license"),
        },
    ];

    const { showAdvancedSearch, handleAdvancedSearchToggle, setShowAdvancedSearch } = useAdvancedSearch({
        searchFields: componentAdvancedSearchFields,
        quickSearchField: "searchTerm",
        setSearchCriteria: setSearchComponentCriteria,
        initialCriteria: INITIAL_COMPONENT_SEARCH_CRITERIA,
    });

    const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
        handleItemsPerPageChange(newItemsPerPage);
        setCurrentComponentPage(1);
    };

    const getComponentListData = useMutationData(
        queryKeys.getListComponents,
        { method: "POST" },
        {
            onError: (error) => {
                console.error("Error during get data:", error);
                showErrorToast(t("common.get-component-list-error"));
            },
        },
    );

    const fetchComponentData = (data, page = currentComponentPage) => {
        const effectiveData = resolveSearchCriteria(data, {
            quickSearchField: "searchTerm",
            isAdvancedPanelOpen: showAdvancedSearch,
        });
        const bodyRequest = {};

        if (effectiveData.searchTerm && effectiveData.searchTerm.trim() !== "") {
            bodyRequest["searchText"] = effectiveData.searchTerm;
        }
        if (effectiveData.componentVersion && effectiveData.componentVersion.trim() !== "") {
            bodyRequest["componentVersion"] = effectiveData.componentVersion;
        }
        if (effectiveData.licenseIdentifier && effectiveData.licenseIdentifier.trim() !== "") {
            bodyRequest["licenseIdentifier"] = effectiveData.licenseIdentifier;
        }

        const startRow = itemsPerPage * (page - 1);
        bodyRequest["pageSize"] = itemsPerPage;
        bodyRequest["startRow"] = startRow;
        const requestId = ++activeRequestIdRef.current;
        getComponentListData.mutate(bodyRequest, {
            onSuccess: (response) => {
                if (requestId !== activeRequestIdRef.current) {
                    return;
                }
                if (typeof response !== "string") {
                    setComponentList(response.object);
                    setTotalComponentData(response.objectCount);
                    setTotalComponentPages(Math.ceil(response.objectCount / itemsPerPage));
                }
            },
        });
    };

    useEffect(() => {
        if (mode === "preview-detail") {
            const filteredAllSelected = allSelectedComponents.filter(comp =>
                selectedComponents.some(selectedComp =>
                    (selectedComp.componentName || '') === (comp.componentName || '') &&
                    (selectedComp.componentVersion || '') === (comp.componentVersion || '') &&
                    (selectedComp.licenseId || '') === (comp.licenseId || '') &&
                    (selectedComp.licenseIdentifier || '') === (comp.licenseIdentifier || '') &&
                    (selectedComp.licenseName || '') === (comp.licenseName || '') &&
                    (selectedComp.licenseTypeCd || '') === (comp.licenseTypeCd || '')
                )
            );
            const filteredSeqs = tempSelectedComponentSeq.filter(seq =>
                selectedComponents.some(comp =>
                    comp.componentSeq === seq
                )
            );
            setAllSelectedComponents(filteredAllSelected);
            setTempSelectedComponentSeq(filteredSeqs);
        }
    }, [selectedComponents]);

    useEffect(() => {
        if (isOpen) {
            const isOpening = !wasOpenRef.current;
            wasOpenRef.current = true;

            setShowAdvancedSearch(false);
            setSearchComponentCriteria({
                searchTerm: "",
            });
            setDebouncedSearchCriteria({
                searchTerm: "",
            });

            const isAddPreviewMode = mode === "add-preview" || (selectedComponents.length > 0 && selectedComponents.some(comp => comp.hasOwnProperty('componentSeq') && !comp.hasOwnProperty('projectPreviewComponentSeq')));

            if (isAddPreviewMode) {
                const existingSeqs = selectedComponents.map(comp => comp.componentSeq);
                setTempSelectedComponentSeq(existingSeqs);
                setSelectedComponentItems(selectedComponents);
                setAllSelectedComponents(selectedComponents);

                const initialMap = new Map();
                selectedComponents.forEach(comp => {
                    initialMap.set(comp.componentSeq, comp);
                });
                setSelectedComponentsFromAllPages(initialMap);
            } else if (mode === "preview-detail") {
                // For preview-detail mode, initialize with projectPreviewComponents
                const existingSeqs = [];
                setTempSelectedComponentSeq(existingSeqs);
                setSelectedComponentItems(selectedComponents);
                setAllSelectedComponents(selectedComponents);

                const initialMap = new Map();
                selectedComponents.forEach(comp => {
                    const key = `${comp.componentName}-${comp.componentVersion}-${comp.licenseId}-${comp.licenseIdentifier}-${comp.licenseName}-${comp.licenseTypeCd}`;
                    initialMap.set(key, comp);
                });
                setSelectedComponentsFromAllPages(initialMap);
            } else {
                setTempSelectedComponentSeq([]);
                setSelectedComponentItems([]);
                setAllSelectedComponents([]);
                setSelectedComponentsFromAllPages(new Map());
            }
            setIsInitialized(false);
            setCurrentComponentPage(1);
            isFirstSearchRenderRef.current = true;
            if (isOpening) {
                fetchComponentData(INITIAL_COMPONENT_SEARCH_CRITERIA, 1);
            }
        } else {
            wasOpenRef.current = false;
        }
    }, [isOpen, selectedComponents, mode]);

    useEffect(() => {
        if (!isOpen) return;

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        if (isFirstSearchRenderRef.current) {
            setIsSearching(false);
            return;
        }

        const criteriaKeys = new Set([
            ...Object.keys(searchComponentCriteria),
            ...Object.keys(debouncedSearchCriteria),
        ]);
        const isCriteriaSynced = [...criteriaKeys].every(
            (key) => (searchComponentCriteria[key] ?? "") === (debouncedSearchCriteria[key] ?? ""),
        );
        if (isCriteriaSynced) {
            setIsSearching(false);
            return;
        }

        const hasActualChange = Object.values(searchComponentCriteria).some((value) => value && value !== "");
        if (hasActualChange || searchComponentCriteria?.searchTerm === "") {
            setIsSearching(true);
            debounceTimerRef.current = setTimeout(() => {
                setDebouncedSearchCriteria(searchComponentCriteria);
                setIsSearching(false);
            }, 500);
        } else {
            setIsSearching(false);
        }

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [searchComponentCriteria, debouncedSearchCriteria, isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        // Intentionally exclude `debouncedSearchCriteria` from deps:
        // filter/advanced-search changes reset the page and trigger the fetch
        // in the effect below (avoids double-fetch).
        fetchComponentData(debouncedSearchCriteria);
    }, [currentComponentPage, itemsPerPage]);

    useEffect(() => {
        if (!isOpen) return;

        if (isFirstSearchRenderRef.current) {
            isFirstSearchRenderRef.current = false;
            return;
        }

        if (currentComponentPage !== 1) {
            setCurrentComponentPage(1);
        } else {
            fetchComponentData(debouncedSearchCriteria);
        }
    }, [debouncedSearchCriteria, showAdvancedSearch]);

    useEffect(() => {
        if (isOpen && componentList.length > 0) {
            const isPreviewDetailMode = mode === "preview-detail";
            const isAddPreviewMode = mode === "add-preview" || (selectedComponents.length > 0 && selectedComponents.some(comp => comp.hasOwnProperty('componentSeq') && !comp.hasOwnProperty('projectPreviewComponentSeq')));

            if (isPreviewDetailMode) {
                const matchesComponent = (a, b) =>
                    (a.componentName || '') === (b.componentName || '') &&
                    (a.componentVersion || '') === (b.componentVersion || '') &&
                    (a.licenseId || '') === (b.licenseId || '') &&
                    (a.licenseIdentifier || '') === (b.licenseIdentifier || '') &&
                    (a.licenseName || '') === (b.licenseName || '') &&
                    (a.licenseTypeCd || '') === (b.licenseTypeCd || '');

                const getComponentKey = (comp) =>
                    `${comp.componentName}-${comp.componentVersion}-${comp.licenseId}-${comp.licenseIdentifier}-${comp.licenseName}-${comp.licenseTypeCd}`;

                const allExistingComponents = [];

                componentList.forEach(comp => {
                    const isAlreadySelected = allSelectedComponents.some(selected => matchesComponent(selected, comp));
                    if (isAlreadySelected) {
                        allExistingComponents.push(comp);
                    }
                });

                allSelectedComponents.forEach(selectedComp => {
                    const isInCurrentPage = componentList.some(comp => matchesComponent(comp, selectedComp));
                    const isAlreadyIncluded = allExistingComponents.some(comp => matchesComponent(comp, selectedComp));
                    if (!isInCurrentPage && !isAlreadyIncluded) {
                        allExistingComponents.push(selectedComp);
                    }
                });

                const actualSelectedSeqs = allExistingComponents.map(comp => comp.componentSeq || getComponentKey(comp));
                setTempSelectedComponentSeq(actualSelectedSeqs);
                setAllSelectedComponents(allExistingComponents);
                setSelectedComponentItems(allExistingComponents);

                const initialMap = new Map();
                allExistingComponents.forEach(comp => {
                    initialMap.set(getComponentKey(comp), comp);
                });
                setSelectedComponentsFromAllPages(initialMap);
            } else if (isAddPreviewMode) {
                
                const existingComponentSeqs = componentList
                    .filter(comp => selectedComponents.some(selectedComp => selectedComp.componentSeq === comp.componentSeq))
                    .map(comp => comp.componentSeq);

                let newSelectedSeqs = [...tempSelectedComponentSeq];

                existingComponentSeqs.forEach(seq => {
                    if (!newSelectedSeqs.includes(seq)) {
                        newSelectedSeqs.push(seq);
                    }
                });

                const allSelectedComps = [];

                componentList.forEach(comp => {
                    if (newSelectedSeqs.includes(comp.componentSeq)) {
                        allSelectedComps.push(comp);
                    }
                });

                selectedComponents.forEach(selectedComp => {
                    if (newSelectedSeqs.includes(selectedComp.componentSeq) && !componentList.some(comp => comp.componentSeq === selectedComp.componentSeq)) {
                        allSelectedComps.push(selectedComp);
                    }
                });

                newSelectedSeqs.forEach(seq => {
                    const foundInCurrentPage = componentList.some(comp => comp.componentSeq === seq);
                    const foundInProps = selectedComponents.some(comp => comp.componentSeq === seq);
                    const foundInAllSelectedComps = allSelectedComps.some(comp => comp.componentSeq === seq);

                    if (!foundInCurrentPage && !foundInProps && !foundInAllSelectedComps) {
                        const fullComp = selectedComponentsFromAllPages.get(seq);
                        if (fullComp) {
                            allSelectedComps.push(fullComp);
                        }
                    }
                });

                setAllSelectedComponents(allSelectedComps);
                setSelectedComponentItems(allSelectedComps);

                const newSelectedComponentsMap = new Map();
                allSelectedComps.forEach(comp => {
                    newSelectedComponentsMap.set(comp.componentSeq, comp);
                });
                setSelectedComponentsFromAllPages(newSelectedComponentsMap);
            } else {
                setTempSelectedComponentSeq([]);
                setSelectedComponentsFromAllPages(new Map());
                setAllSelectedComponents([]);
                setSelectedComponentItems([]);
            }

            if (!isInitialized) {
                setIsInitialized(true);
            }
        }
    }, [componentList, isOpen, selectedComponents]);

    const componentTableColumns = ["componentName", "componentVersion", "componentLicense"];
    const componentTableColumnConfig = {
        componentName: {
            style: {
                width: "40%",
                whiteSpace: "normal",
                overflowWrap: "initial",
            },
            headerRender: () => t("status.component-name"),
            customRender: (value, row) => <div>{row.componentName}</div>,
        },
        componentVersion: {
            style: {
                width: "30%",
                whiteSpace: "normal",
                overflowWrap: "initial",
            },
            headerRender: () => t("status.component-version"),
            customRender: (value, row) => <div>{row.componentVersion}</div>,
        },
        componentLicense: {
            style: {
                width: "30%",
                whiteSpace: "normal",
                overflowWrap: "initial",
            },
            headerRender: () => t("status.component-license"),
            customRender: (value, row) => <div>{row.licenseIdentifier}</div>,
        },
    };

    const resetPaginationState = () => {
        setCurrentComponentPage(1);
        setComponentList([]);
        setTotalComponentData(0);
        setTotalComponentPages(0);
        activeRequestIdRef.current += 1;
    };

    const handleCancel = () => {
        setTempSelectedComponentSeq([]);
        setSelectedComponentItems([]);
        setAllSelectedComponents([]);
        setSelectedComponentsFromAllPages(new Map());
        setIsInitialized(false);
        resetPaginationState();
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        onClose();
    };

    const handleReset = () => {
        setSearchComponentCriteria({
            searchTerm: "",
        });
    };



    const handleSelect = () => {

        // Check mode based on prop
        const isPreviewDetailMode = mode === "preview-detail";
        const isAddPreviewMode = mode === "add-preview" || (selectedComponents.length > 0 && selectedComponents.some(comp => comp.hasOwnProperty('componentSeq') && !comp.hasOwnProperty('projectPreviewComponentSeq')));

        if (isPreviewDetailMode) {
            const newComponents = allSelectedComponents.filter(comp =>
                !selectedComponents.some(existingComp =>
                    (existingComp.componentName || '') === (comp.componentName || '') &&
                    (existingComp.componentVersion || '') === (comp.componentVersion || '') &&
                    (existingComp.licenseId || '') === (comp.licenseId || '') &&
                    (existingComp.licenseIdentifier || '') === (comp.licenseIdentifier || '') &&
                    (existingComp.licenseName || '') === (comp.licenseName || '') &&
                    (existingComp.licenseTypeCd || '') === (comp.licenseTypeCd || '')
                )
            );

            const deselectedComponents = selectedComponents.filter(existingComp => {
                return !allSelectedComponents.some(selectedComp =>
                    (existingComp.componentName || '') === (selectedComp.componentName || '') &&
                    (existingComp.componentVersion || '') === (selectedComp.componentVersion || '') &&
                    (existingComp.licenseId || '') === (selectedComp.licenseId || '') &&
                    (existingComp.licenseIdentifier || '') === (selectedComp.licenseIdentifier || '') &&
                    (existingComp.licenseName || '') === (selectedComp.licenseName || '') &&
                    (existingComp.licenseTypeCd || '') === (selectedComp.licenseTypeCd || '')
                );
            });

            onSelect({
                newComponents,
                deselectedComponents
            });
        } else {
            onSelect(allSelectedComponents);
        }

        setIsInitialized(false);
        resetPaginationState();
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        onClose();
    };

    const handleOnRowComponentListClick = (data) => {
        const isSelected = tempSelectedComponentSeq.includes(data.componentSeq);
        let newSelectedSeqs;

        if (isSelected) {
            // Remove from selection
            newSelectedSeqs = tempSelectedComponentSeq.filter(seq => seq !== data.componentSeq);
        } else {
            // Add to selection
            newSelectedSeqs = [...tempSelectedComponentSeq, data.componentSeq];
        }

        setTempSelectedComponentSeq(newSelectedSeqs);

        // Check mode based on prop
        const isPreviewDetailMode = mode === "preview-detail";
        const isAddPreviewMode = mode === "add-preview" || (selectedComponents.length > 0 && selectedComponents.some(comp => comp.hasOwnProperty('componentSeq') && !comp.hasOwnProperty('projectPreviewComponentSeq')));

        if (isPreviewDetailMode) {
            // PreviewDetail mode - use array-based tracking with 6 attributes
            let newAllSelectedComponents;

            if (isSelected) {
                // Remove component using 6 attributes for comparison
                newAllSelectedComponents = allSelectedComponents.filter(item =>
                    !((item.componentName || '') === (data.componentName || '') &&
                        (item.componentVersion || '') === (data.componentVersion || '') &&
                        (item.licenseId || '') === (data.licenseId || '') &&
                        (item.licenseIdentifier || '') === (data.licenseIdentifier || '') &&
                        (item.licenseName || '') === (data.licenseName || '') &&
                        (item.licenseTypeCd || '') === (data.licenseTypeCd || ''))
                );
            } else {
                // Add component
                newAllSelectedComponents = [...allSelectedComponents, data];
            }

            setAllSelectedComponents(newAllSelectedComponents);
            setSelectedComponentItems(newAllSelectedComponents);

            // Also update the map for consistency (using 6 attributes as key)
            const newSelectedComponentsMap = new Map();
            newAllSelectedComponents.forEach(comp => {
                const key = `${comp.componentName}-${comp.componentVersion}-${comp.licenseId}-${comp.licenseIdentifier}-${comp.licenseName}-${comp.licenseTypeCd}`;
                newSelectedComponentsMap.set(key, comp);
            });
            setSelectedComponentsFromAllPages(newSelectedComponentsMap);
        } else if (isAddPreviewMode) {
            // AddPreview mode - simple array-based tracking
            let newAllSelectedComponents;

            if (isSelected) {
                newAllSelectedComponents = allSelectedComponents.filter(item => item.componentSeq !== data.componentSeq);
            } else {
                newAllSelectedComponents = [...allSelectedComponents, data];
            }

            setAllSelectedComponents(newAllSelectedComponents);
            setSelectedComponentItems(newAllSelectedComponents);

            // Also update the map for consistency (using componentSeq as key for AddPreview)
            const newSelectedComponentsMap = new Map();
            newAllSelectedComponents.forEach(comp => {
                newSelectedComponentsMap.set(comp.componentSeq, comp);
            });
            setSelectedComponentsFromAllPages(newSelectedComponentsMap);
        } else {
            // No existing components - simple array-based tracking
            let newAllSelectedComponents;

            if (isSelected) {
                newAllSelectedComponents = allSelectedComponents.filter(item => item.componentSeq !== data.componentSeq);
            } else {
                newAllSelectedComponents = [...allSelectedComponents, data];
            }

            setAllSelectedComponents(newAllSelectedComponents);
            setSelectedComponentItems(newAllSelectedComponents);
        }
    };

    const handleOnCheckboxChange = (data) => {
        // Get current page component sequences
        const currentPageSeqs = new Set(data);

        // Check mode based on prop
        const isPreviewDetailMode = mode === "preview-detail";
        const isAddPreviewMode = mode === "add-preview" || (selectedComponents.length > 0 && selectedComponents.some(comp => comp.hasOwnProperty('componentSeq') && !comp.hasOwnProperty('projectPreviewComponentSeq')));

        // Get sequences from other pages that are not in current page
        let otherPageSeqs;
        if (isPreviewDetailMode) {
            // For PreviewDetail, filter out componentSeq that are in current page
            otherPageSeqs = tempSelectedComponentSeq.filter(seq =>
                !componentList.some(item => item.componentSeq === seq)
            );
        } else {
            // For add-preview mode, filter out componentSeq that are in current page
            otherPageSeqs = tempSelectedComponentSeq.filter(seq =>
                !componentList.some(item => item.componentSeq === seq)
            );
        }

        // Combine sequences
        const newSelectedSeqs = [...otherPageSeqs, ...currentPageSeqs];
        setTempSelectedComponentSeq(newSelectedSeqs);

        if (isPreviewDetailMode) {
            // Get current page components that are selected
            const currentPageComponents = componentList.filter(item => currentPageSeqs.has(item.componentSeq));

            // Get components from other pages that are not in current page (using 6 attributes)
            const otherPageComponents = allSelectedComponents.filter(comp =>
                !componentList.some(item =>
                    (item.componentName || '') === (comp.componentName || '') &&
                    (item.componentVersion || '') === (comp.componentVersion || '') &&
                    (item.licenseId || '') === (comp.licenseId || '') &&
                    (item.licenseIdentifier || '') === (comp.licenseIdentifier || '') &&
                    (item.licenseName || '') === (comp.licenseName || '') &&
                    (item.licenseTypeCd || '') === (comp.licenseTypeCd || '')
                )
            );

            // Combine all selected components
            const newAllSelectedComponents = [...otherPageComponents, ...currentPageComponents];
            setAllSelectedComponents(newAllSelectedComponents);
            setSelectedComponentItems(newAllSelectedComponents);

            // Also update the map for consistency (using 6 attributes as key)
            const newSelectedComponentsMap = new Map();
            newAllSelectedComponents.forEach(comp => {
                const key = `${comp.componentName}-${comp.componentVersion}-${comp.licenseId}-${comp.licenseIdentifier}-${comp.licenseName}-${comp.licenseTypeCd}`;
                newSelectedComponentsMap.set(key, comp);
            });
            setSelectedComponentsFromAllPages(newSelectedComponentsMap);

            const actualSelectedSeqs = newAllSelectedComponents.map(comp =>
                comp.componentSeq || `${comp.componentName}-${comp.componentVersion}-${comp.licenseId}-${comp.licenseIdentifier}-${comp.licenseName}-${comp.licenseTypeCd}`
            );
            setTempSelectedComponentSeq(actualSelectedSeqs);
        } else if (isAddPreviewMode) {
            // Get current page components that are selected
            const currentPageComponents = componentList.filter(item => currentPageSeqs.has(item.componentSeq));

            // Get components from other pages that are not in current page
            const otherPageComponents = allSelectedComponents.filter(comp =>
                !componentList.some(item => item.componentSeq === comp.componentSeq)
            );

            // Combine all selected components
            const newAllSelectedComponents = [...otherPageComponents, ...currentPageComponents];
            setAllSelectedComponents(newAllSelectedComponents);
            setSelectedComponentItems(newAllSelectedComponents);

            // Also update the map for consistency (using componentSeq as key for AddPreview)
            const newSelectedComponentsMap = new Map();
            newAllSelectedComponents.forEach(comp => {
                newSelectedComponentsMap.set(comp.componentSeq, comp);
            });
            setSelectedComponentsFromAllPages(newSelectedComponentsMap);

            // Update tempSelectedComponentSeq to match the actual selected components
            const actualSelectedSeqs = newAllSelectedComponents.map(comp => comp.componentSeq);
            setTempSelectedComponentSeq(actualSelectedSeqs);
        } else {
            const currentPageComponents = componentList.filter(item => currentPageSeqs.has(item.componentSeq));

            const otherPageComponents = allSelectedComponents.filter(comp =>
                !componentList.some(item => item.componentSeq === comp.componentSeq)
            );

            const newAllSelectedComponents = [...otherPageComponents, ...currentPageComponents];
            setAllSelectedComponents(newAllSelectedComponents);
            setSelectedComponentItems(newAllSelectedComponents);
        }
    };

    useEscapeKey(handleCancel, isOpen);

    useEffect(() => {
        if (!isOpen) return;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [isOpen]);

  if (!isOpen) return null;

    return (
        <div
            className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-start lg:justify-center p-4 backdrop-blur-[1px] overflow-x-auto overflow-y-hidden`}
            data-testid={`${dataTestId}-overlay`}
        >
            <div className={`${colors.bgModal} max-h-[calc(100vh-2rem)] w-[800px] min-w-[800px] flex-shrink-0 rounded-lg shadow-xl flex flex-col overflow-hidden`} data-testid={dataTestId}>
                {/* Modal Header */}
                <div className={`px-6 py-4 flex-shrink-0`} data-testid={`${dataTestId}-header`}>
                    <div className="flex items-center justify-between">
                        <h2 className={`${colors.textPrimary} text-xl font-bold`} data-testid={`${dataTestId}-title`}>{t("common.select-components")}</h2>
                    </div>
                </div>

                {/* Search Section */}
                <div className="space-y-4 px-6 pb-4 flex-shrink-0">
                    {/* Search Inputs */}
                    <div className="grid grid-cols-3 gap-4">
                        <div className="relative md:col-span-2">
                            <MagnifyingGlassIcon
                                className={`absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform ${colors.textSecondary}`}
                            />
                            <input
                                type="text"
                                placeholder={t("common.search-component-name")}
                                value={searchComponentCriteria.searchTerm}
                                onChange={(e) => setSearchComponentCriteria((prev) => ({ ...prev, searchTerm: e.target.value }))}
                                className={`w-full rounded-lg border px-3 py-2 pr-8 pl-10 text-sm focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                                data-testid={fieldInputTestId("search")}
                            />
                            {isSearching && (
                                <div className="absolute top-1/2 right-3 -translate-y-1/2 transform">
                                    <div className={`h-3 w-3 animate-spin rounded-full border-b-2 ${colors.borderLoader}`}></div>
                                </div>
                            )}
                        </div>
                        <button
                            onClick={handleAdvancedSearchToggle}
                            className={`inline-flex items-center justify-center border px-4 py-2 ${colors.borderOutLineButton} rounded-md text-sm font-medium shadow-sm ${colors.textActive} ${colors.hoverOutlineModalSelection} whitespace-nowrap`}
                            data-testid={fieldInputTestId("advanced-search-toggle")}
                        >
                            <AdjustIcon className="mr-2 h-4 w-4" />
                            <span className="hidden sm:inline">{t("common.advanced-search")}</span>
                            <span className="sm:hidden">Advanced</span>
                        </button>
                    </div>
                </div>

                {/* Advanced Search */}
                {showAdvancedSearch && (
                    <div className="relative mx-6 flex-shrink-0">
                        {/* Arrow pointer pointing to Advanced button */}
                        <div
                            className={`absolute -top-2 z-10 h-0 w-0`}
                            style={{
                                right: "calc(33.333% / 2 - 16px)", // Center of the 3rd column minus half the triangle width
                                borderLeft: "8px solid transparent",
                                borderRight: "8px solid transparent",
                                borderBottom: `8px solid #374151`,
                                filter: `drop-shadow(0 0 0 1px #292D33)`,
                            }}
                        ></div>

                        <AdvancedSearch
                            title={t("common.advanced-search")}
                            searchFields={componentAdvancedSearchFields}
                            searchHandler={(searchData) => {
                                setSearchComponentCriteria((prev) => ({
                                    ...prev,
                                    ...searchData,
                                }));
                            }}
                            withoutSearch={true}
                            gridCols="md:grid-cols-2"
                        />
                    </div>
                )}

                {/* Table Section - Scrollable */}
                <div className="flex flex-1 flex-col px-6 min-h-0 overflow-hidden" data-testid={`${dataTestId}-content`}>
                    <TableWithPagination
                        columns={componentTableColumns}
                        columnConfig={componentTableColumnConfig}
                        data={componentList}
                        currentPage={currentComponentPage}
                        setCurrentPage={setCurrentComponentPage}
                        loading={getComponentListData.isPending || isSearching}
                        rowKey="componentSeq"
                        totalPages={totalComponentPages}
                        totalData={totalComponentData}
                        maxRowsPerPage={itemsPerPage}
                        hasCheckbox={true}
                        selectedRows={tempSelectedComponentSeq}
                        onCheckboxChange={handleOnCheckboxChange}
                        onRowClick={handleOnRowComponentListClick}
                        onItemsPerPageChange={handleItemsPerPageChangeWithReset }
                        showPagination={true}
                        scrollableBody={true}
                        showItemsPerPageSelector={true}
                    />
                </div>

                {/* Modal Footer - Sticky */}
                <div className={`px-6 py-4 ${colors.borderPrimary} flex-shrink-0`} data-testid={`${dataTestId}-footer`}>
                    <div className="flex items-center justify-end">
                        <div className="flex items-center space-x-3">
                            <button
                                data-testid={`${dataTestId}-cancel-button`}
                                onClick={handleCancel}
                                className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${colors.borderActiveDashed} ${colors.textActive} ${colors.bgButton} hover:opacity-80`}
                            >
                                {t("common.cancel-button")}
                            </button>
                            <PrimaryButton
                                data-testid={`${dataTestId}-confirm-button`}
                                onClick={handleSelect}
                                disabled={mode === "preview-detail" ? selectedComponentItems.length === 0 : tempSelectedComponentSeq.length === 0}
                            >
                                {t("common.select")} ({mode === "preview-detail" ? allSelectedComponents.length : tempSelectedComponentSeq.length})
                            </PrimaryButton>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SelectComponentModal; 